import { AppDispatch, RootState } from '../../../store'
import { camelize } from 'casing'
import {
  ActionItem,
  OpportunityNote,
  LockNotesPayload,
  FetchNotesOptions,
  Risk,
} from './reducer'
import {
  deleteLockNote,
  getCriticalActions,
  getNotes,
  getRecordById,
  getRisks,
  postLockNote,
  postNote,
  updateActionStatus,
} from '../../api'
import { AxiosError, AxiosResponse } from 'axios'
import { errorLogger } from '../../../common/utils/errorLogger'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { LoadingTypes, startLoading, stopLoading } from '@labrav/common'
import { PropensityRow } from '../Propensity/reducer'

export enum SmartGtmActionTypes {
  SET_CRITICAL_ACTIONS = 'SET_CRITICAL_ACTIONS',
  CLEAR_CRITICAL_ACTIONS = 'CLEAR_CRITICAL_ACTIONS',
  UPDATE_CRITICAL_ACTION = 'UPDATE_CRITICAL_ACTION',
  SET_SELECTED_ACTION_ID = 'SET_SELECTED_ACTION_ID',
  CLEAR_SELECTED_ACTION_ID = 'CLEAR_SELECTED_ACTION_ID',
  SET_OPEN_AI_DRAWER = 'SET_OPEN_AI_DRAWER',
  CLEAR_OPEN_AI_DRAWER = 'CLEAR_OPEN_AI_DRAWER',
  UPDATE_CRITICAL_ACTION_STATUS = 'UPDATE_CRITICAL_ACTION_STATUS',
  SET_SELECTED_ROW = 'SET_SELECTED_ROW',
  CLEAR_SELECTED_ROW = 'CLEAR_SELECTED_ROW',
  SET_OPPORTUNITY_NOTES = 'SET_OPPORTUNITY_NOTES',
  UPDATE_OPPORTUNITY_NOTES = 'UPDATE_OPPORTUNITY_NOTES',
  CLEAR_OPPORTUNITY_NOTES = 'CLEAR_OPPORTUNITY_NOTES',
  SET_RISKS = 'SET_RISKS',
  CLEAR_RISKS = 'CLEAR_RISKS',
  SET_LAST_PROMPT = 'SET_LAST_PROMPT',
  CLEAR_LAST_PROMPT = 'CLEAR_LAST_PROMPT',
  SET_AI_AUTO_PROMPT = 'SET_AI_AUTO_PROMPT',
  CLEAR_AI_AUTO_PROMPT = 'CLEAR_AI_AUTO_PROMPT',
  SET_AWS_USER_ID = 'SET_AWS_USER_ID',
  CLEAR_AWS_USER_ID = 'CLEAR_AWS_USER_ID',
  SET_HUBSPOT_PARTNER_ID = 'SET_HUBSPOT_PARTNER_ID',
  CLEAR_HUBSPOT_PARTNER_ID = 'CLEAR_HUBSPOT_PARTNER_ID',
}

const NOTES_LOCK_ERROR_CODES = new Set([
  'ERR_NOTES_LOCKED',
  'ERR_NOTES_CONFLICT',
])

const DEFAULT_LOCK_DURATION_SECONDS = 15 * 60

const normalizeOpportunityNote = (
  data: Partial<OpportunityNote>
): OpportunityNote => ({
  id: data.id ?? '',
  opportunityId: data.opportunityId ?? '',
  userId: data.userId ?? '',
  notes: data.notes ?? '',
  history: data.history ?? [],
  createdAt: data.createdAt ?? '',
  updatedAt: data.updatedAt ?? '',
  lockOwnerId: data.lockOwnerId ?? null,
  lockExpiresAt: data.lockExpiresAt ?? null,
})

const refetchNotesOnLockError = async (
  dispatch: AppDispatch,
  partnerId: string,
  opportunityId: string,
  errorResponse?: AxiosResponse<ErrorResponse>
) => {
  const errorCode = errorResponse?.data?.code
  if (!NOTES_LOCK_ERROR_CODES.has(errorCode ?? '')) {
    return
  }
  try {
    await dispatch(
      fetchOpportunityNotes(partnerId, opportunityId, { withLoader: false })
    )
  } catch {
    return
  }
}

export const getActionDetails =
  (partnerId: string, operationId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_ACTION_DETAILS))
    try {
      const response = await getCriticalActions(partnerId, operationId)
      const data = camelize(response.data.actions)
      dispatch(setCriticalActions(data))
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_ACTION_DETAILS))
    }
  }

export const fetchRecordById =
  (partnerId: string, Id: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getRecordById(partnerId, Id)
      const data = camelize(response.data)
      dispatch(setSelectedRow(data))
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const updateCriticalActionStatus =
  (
    partnerId: string,
    operationId: string,
    actionId: string,
    isHidden: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const message = isHidden
        ? 'Action hidden successfully'
        : 'Action unhidden successfully'
      await updateActionStatus(partnerId, operationId, actionId, isHidden)
      dispatch(
        updateAppAlert({
          message: message,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchOpportunityNotes =
  (
    partnerId: string,
    opportunityId: string,
    options: FetchNotesOptions = { withLoader: true }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const shouldShowLoader = options.withLoader ?? true
    if (shouldShowLoader) {
      await dispatch(startLoading(LoadingTypes.GENERAL))
    }
    try {
      const response = await getNotes(partnerId, opportunityId)
      const data = camelize(response.data) as Partial<OpportunityNote>
      const normalized = normalizeOpportunityNote(data)
      dispatch(setOpportunityNotes(normalized))
      return normalized
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      if (shouldShowLoader) {
        dispatch(stopLoading(LoadingTypes.GENERAL))
      }
    }
  }

export const lockOpportunityNotes =
  (partnerId: string, opportunityId: string, payload: LockNotesPayload) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const requestBody: {
        user_id: string
        lock_duration_seconds: number
      } = {
        user_id: payload.userId,
        lock_duration_seconds:
          payload.lockDurationSeconds ?? DEFAULT_LOCK_DURATION_SECONDS,
      }
      const response = await postLockNote(partnerId, opportunityId, requestBody)
      const data = camelize(response.data) as Partial<OpportunityNote>
      dispatch(updateOpportunityNotes(data))
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      await refetchNotesOnLockError(
        dispatch,
        partnerId,
        opportunityId,
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const releaseOpportunityNotesLock =
  (partnerId: string, opportunityId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      await deleteLockNote(partnerId, opportunityId)
      dispatch(
        updateOpportunityNotes({
          lockOwnerId: null,
          lockExpiresAt: null,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      await refetchNotesOnLockError(
        dispatch,
        partnerId,
        opportunityId,
        errorResponse
      )
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const saveOpportunityNote =
  (partnerId: string, opportunityId: string, note: string, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await postNote(partnerId, opportunityId, {
        notes: note,
        user_id: userId,
      })
      const data = camelize(response.data) as Partial<OpportunityNote>
      const normalized = normalizeOpportunityNote(data)
      dispatch(setOpportunityNotes(normalized))
      return normalized
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchRisks =
  (partnerId: string, opportunityId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getRisks(partnerId, opportunityId)
      const data = camelize(response.data.risks)
      dispatch(setRisks(data))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
export const setCriticalActions = (criticalActions: ActionItem[]) => ({
  type: SmartGtmActionTypes.SET_CRITICAL_ACTIONS,
  payload: criticalActions,
})
export const clearCriticalActions = () => ({
  type: SmartGtmActionTypes.CLEAR_CRITICAL_ACTIONS,
})

export const setSelectedActionId = (selectedActionId: string | null) => ({
  type: SmartGtmActionTypes.SET_SELECTED_ACTION_ID,
  payload: selectedActionId,
})
export const clearSelectedActionId = () => ({
  type: SmartGtmActionTypes.CLEAR_SELECTED_ACTION_ID,
})
export const setOpenAiDrawer = (openAiDrawer: boolean) => ({
  type: SmartGtmActionTypes.SET_OPEN_AI_DRAWER,
  payload: openAiDrawer,
})
export const clearOpenAiDrawer = () => ({
  type: SmartGtmActionTypes.CLEAR_OPEN_AI_DRAWER,
})
export const setSelectedRow = (selectedRow: PropensityRow | null) => ({
  type: SmartGtmActionTypes.SET_SELECTED_ROW,
  payload: selectedRow,
})
export const clearSelectedRow = () => ({
  type: SmartGtmActionTypes.CLEAR_SELECTED_ROW,
})
export const setOpportunityNotes = (opportunityNotes: OpportunityNote) => ({
  type: SmartGtmActionTypes.SET_OPPORTUNITY_NOTES,
  payload: opportunityNotes,
})
export const setRisks = (risks: Risk[]) => ({
  type: SmartGtmActionTypes.SET_RISKS,
  payload: risks,
})
export const clearRisks = () => ({
  type: SmartGtmActionTypes.CLEAR_RISKS,
})
export const setLastPrompt = (lastPrompt: string) => ({
  type: SmartGtmActionTypes.SET_LAST_PROMPT,
  payload: lastPrompt,
})
export const setAiAutoPrompt = (prompt: string) => ({
  type: SmartGtmActionTypes.SET_AI_AUTO_PROMPT,
  payload: prompt,
})
export const clearAiAutoPrompt = () => ({
  type: SmartGtmActionTypes.CLEAR_AI_AUTO_PROMPT,
})
export const clearLastPrompt = () => ({
  type: SmartGtmActionTypes.CLEAR_LAST_PROMPT,
})
export const updateOpportunityNotes = (
  opportunityNotes: Partial<OpportunityNote>
) => ({
  type: SmartGtmActionTypes.UPDATE_OPPORTUNITY_NOTES,
  payload: opportunityNotes,
})
export const clearOpportunityNotes = () => ({
  type: SmartGtmActionTypes.CLEAR_OPPORTUNITY_NOTES,
})
export const setAwsUserId = (awsUserId: string) => ({
  type: SmartGtmActionTypes.SET_AWS_USER_ID,
  payload: awsUserId,
})
export const clearAwsUserId = () => ({
  type: SmartGtmActionTypes.CLEAR_AWS_USER_ID,
})
export const setHubspotPartnerId = (hubspotPartnerId: string) => ({
  type: SmartGtmActionTypes.SET_HUBSPOT_PARTNER_ID,
  payload: hubspotPartnerId,
})
export const clearHubspotPartnerId = () => ({
  type: SmartGtmActionTypes.CLEAR_HUBSPOT_PARTNER_ID,
})
